import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col, Card, CardBody, Input, Label } from "reactstrap";
import { toast } from "react-toastify";
import { FetchAPI } from "../../../../../helpers/APIdata/fetchAPI";
import DeleteModal from "../../../../../Components/Common/DeleteModal";
import {
  getStaffList,
  getTaskStatusListCrmIT,
  getTaskPriorityList,
  getTaskTypeList,
} from "../../../../../store/actions";
import {
  filterActiveStaff,
  staffDisplayName,
} from "../../../../../Components/Common/staffActive";

// Change #100261 - "need ability to import a task list template. Say we have
// set tasks for this like order kit, setup kit, dispatch kit. We need to add
// these in the settings area to define the templates, then I can apply the
// template to mass create."
//
// Settings > Tasks > Task List Templates. A template is a name plus an ordered
// list of tasks; only each task's title is required. It is applied from a
// job's Tasks tab ("Apply template"), which creates every task on the job.

const blankItem = () => ({
  key: `${Date.now()}-${Math.random()}`,
  Title: "",
  Description: "",
  TaskStatus_Id: "",
  TaskType_Id: "",
  TaskPriority_Id: "",
  Staff_Id: "",
  DueDateTimeAddDays: "",
});

const blankTemplate = () => ({
  Id: "",
  Name: "",
  Description: "",
  items: [blankItem()],
});

const fromApi = (t) => ({
  Id: t.Id,
  Name: t.Name || "",
  Description: t.Description || "",
  items: (t.tasklisttemplateitem_list || []).map((i) => ({
    key: `i${i.Id}`,
    Title: i.Title || "",
    Description: i.Description || "",
    TaskStatus_Id: i.TaskStatus_Id ?? "",
    TaskType_Id: i.TaskType_Id ?? "",
    TaskPriority_Id: i.TaskPriority_Id ?? "",
    Staff_Id: i.Staff_Id ?? "",
    DueDateTimeAddDays: i.DueDateTimeAddDays ?? "",
  })),
});

export default function TaskListTemplate() {
  const dispatch = useDispatch();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notSetUp, setNotSetUp] = useState("");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { staffList, statusList, typeList, priorityList } = useSelector(
    (state) => ({
      staffList: state.Staff.staffList?.staff_list,
      statusList: state.TasksCrmIt.taskStatusListCrmIT?.taskstatus_list,
      typeList: state.TasksCrmIt.taskTypeList?.tasktype_list,
      priorityList: state.TasksCrmIt.taskPriorityList?.taskpriority_list,
    })
  );

  useEffect(() => {
    dispatch(getStaffList());
    dispatch(getTaskStatusListCrmIT());
    dispatch(getTaskPriorityList());
    dispatch(getTaskTypeList());
  }, [dispatch]);

  const staffOptions = useMemo(
    () => filterActiveStaff(staffList || []),
    [staffList]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await FetchAPI("/tasklisttemplate/list", "GET");
      const list = res?.tasklisttemplate_list || [];
      setTemplates(list);
      setNotSetUp(
        res?.status === 200 && !list.length && /not set up/i.test(res?.message || "")
          ? res.message
          : ""
      );
      return list;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (field, value) =>
    setEditing((prev) => ({ ...prev, [field]: value }));

  const setItem = (idx, field, value) =>
    setEditing((prev) => ({
      ...prev,
      items: prev.items.map((it, i) => (i === idx ? { ...it, [field]: value } : it)),
    }));

  const moveItem = (idx, dir) =>
    setEditing((prev) => {
      const items = [...prev.items];
      const to = idx + dir;
      if (to < 0 || to >= items.length) return prev;
      [items[idx], items[to]] = [items[to], items[idx]];
      return { ...prev, items };
    });

  const removeItem = (idx) =>
    setEditing((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== idx),
    }));

  const handleSave = async () => {
    if (!editing?.Name?.trim()) {
      toast.error("Give the template a name");
      return;
    }
    const items = editing.items
      .filter((i) => i.Title.trim())
      .map(({ key, ...rest }) => ({ ...rest, Title: rest.Title.trim() }));
    if (!items.length) {
      toast.error("Add at least one task with a title");
      return;
    }
    setSaving(true);
    try {
      const body = [
        {
          ...(editing.Id ? { Id: editing.Id } : {}),
          Name: editing.Name.trim(),
          Description: editing.Description,
          tasklisttemplateitem_list: items,
        },
      ];
      const res = editing.Id
        ? await FetchAPI("/tasklisttemplate/update", "PUT", body)
        : await FetchAPI("/tasklisttemplate/create", "POST", body);
      if (res?.status !== 200) {
        toast.error(res?.message || "Could not save the template");
        return;
      }
      toast.success(editing.Id ? "Template updated" : "Template created");
      const list = await load();
      const id = res?.TaskListTemplate_Id || editing.Id;
      const saved = list.find((t) => String(t.Id) === String(id));
      setEditing(saved ? fromApi(saved) : null);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleteOpen(false);
    if (!editing?.Id) return;
    const res = await FetchAPI("/tasklisttemplate/delete", "DELETE", [
      { Id: editing.Id },
    ]);
    if (res?.status === 200) {
      toast.success("Template deleted");
      setEditing(null);
      load();
    } else {
      toast.error(res?.message || "Could not delete the template");
    }
  };

  const idSelect = (value, onChange, list, placeholder, labelOf = (x) => x.Name) => (
    <Input
      type="select"
      bsSize="sm"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{placeholder}</option>
      {(list || []).map((x) => (
        <option key={x.Id} value={x.Id}>
          {labelOf(x)}
        </option>
      ))}
    </Input>
  );

  return (
    <>
      <DeleteModal
        show={deleteOpen}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteOpen(false)}
      />

      {notSetUp && (
        <div className="alert alert-warning">
          {notSetUp}. Database delta 185 needs to be run first.
        </div>
      )}

      <Row className="g-3">
        {/* Template list */}
        <Col lg={4}>
          <Card className="mb-0">
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">Task list templates</h6>
                <button
                  type="button"
                  className="btn btn-sm btn-success"
                  onClick={() => setEditing(blankTemplate())}
                >
                  <i className="ri-add-line me-1"></i>New
                </button>
              </div>
              <p className="text-muted small">
                A set of tasks created together on a job - e.g. Order kit, Setup
                kit, Dispatch kit. Apply one from a job's Tasks tab.
              </p>
              {loading && !templates.length ? (
                <div className="text-muted small">Loading...</div>
              ) : !templates.length ? (
                <div className="text-muted small">No templates yet.</div>
              ) : (
                <div className="list-group">
                  {templates.map((t) => (
                    <button
                      key={t.Id}
                      type="button"
                      className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${
                        String(editing?.Id) === String(t.Id) ? "active" : ""
                      }`}
                      onClick={() => setEditing(fromApi(t))}
                    >
                      <span className="text-truncate">{t.Name}</span>
                      <span className="badge bg-secondary-subtle text-secondary ms-2">
                        {t.QtyItem} {Number(t.QtyItem) === 1 ? "task" : "tasks"}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>
        </Col>

        {/* Editor */}
        <Col lg={8}>
          <Card className="mb-0">
            <CardBody>
              {!editing ? (
                <div className="text-center text-muted py-5">
                  <i className="ri-list-check-2 fs-1 d-block mb-2"></i>
                  Choose a template to edit, or create a new one.
                </div>
              ) : (
                <>
                  <Row className="g-2 mb-3">
                    <Col md={5}>
                      <Label className="form-label small mb-1">Template name</Label>
                      <Input
                        value={editing.Name}
                        onChange={(e) => setField("Name", e.target.value)}
                        placeholder="e.g. Kit order"
                      />
                    </Col>
                    <Col md={7}>
                      <Label className="form-label small mb-1">Description</Label>
                      <Input
                        value={editing.Description}
                        onChange={(e) => setField("Description", e.target.value)}
                        placeholder="Optional - what this list is for"
                      />
                    </Col>
                  </Row>

                  <h6 className="mb-2">Tasks</h6>
                  <p className="text-muted small mb-2">
                    Only the title is needed. Anything left blank is left blank on
                    the task, as with New Task. "Due in" sets the due date that
                    many days after the template is applied.
                  </p>

                  {editing.items.map((it, idx) => (
                    <div key={it.key} className="border rounded p-2 mb-2">
                      <div className="d-flex gap-2 align-items-center mb-2">
                        <span className="badge bg-primary-subtle text-primary">
                          {idx + 1}
                        </span>
                        <Input
                          bsSize="sm"
                          value={it.Title}
                          onChange={(e) => setItem(idx, "Title", e.target.value)}
                          placeholder="Task title, e.g. Order kit"
                        />
                        <div className="btn-group btn-group-sm">
                          <button
                            type="button"
                            className="btn btn-light"
                            title="Move up"
                            disabled={idx === 0}
                            onClick={() => moveItem(idx, -1)}
                          >
                            <i className="ri-arrow-up-line"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-light"
                            title="Move down"
                            disabled={idx === editing.items.length - 1}
                            onClick={() => moveItem(idx, 1)}
                          >
                            <i className="ri-arrow-down-line"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-light text-danger"
                            title="Remove task"
                            onClick={() => removeItem(idx)}
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </div>
                      <Row className="g-2">
                        <Col sm={6} md={4}>
                          {idSelect(
                            it.Staff_Id,
                            (v) => setItem(idx, "Staff_Id", v),
                            staffOptions,
                            "Assign to (none)",
                            staffDisplayName
                          )}
                        </Col>
                        <Col sm={6} md={2}>
                          <Input
                            bsSize="sm"
                            type="number"
                            min="0"
                            value={it.DueDateTimeAddDays}
                            onChange={(e) =>
                              setItem(idx, "DueDateTimeAddDays", e.target.value)
                            }
                            placeholder="Due in (days)"
                            title="Due this many days after the template is applied"
                          />
                        </Col>
                        <Col sm={4} md={2}>
                          {idSelect(
                            it.TaskStatus_Id,
                            (v) => setItem(idx, "TaskStatus_Id", v),
                            statusList,
                            "Status"
                          )}
                        </Col>
                        <Col sm={4} md={2}>
                          {idSelect(
                            it.TaskType_Id,
                            (v) => setItem(idx, "TaskType_Id", v),
                            typeList,
                            "Type"
                          )}
                        </Col>
                        <Col sm={4} md={2}>
                          {idSelect(
                            it.TaskPriority_Id,
                            (v) => setItem(idx, "TaskPriority_Id", v),
                            priorityList,
                            "Priority"
                          )}
                        </Col>
                        <Col xs={12}>
                          <Input
                            bsSize="sm"
                            type="textarea"
                            rows={1}
                            value={it.Description}
                            onChange={(e) => setItem(idx, "Description", e.target.value)}
                            placeholder="Description (optional)"
                          />
                        </Col>
                      </Row>
                    </div>
                  ))}

                  <button
                    type="button"
                    className="btn btn-sm btn-soft-primary mb-3"
                    onClick={() =>
                      setEditing((prev) => ({
                        ...prev,
                        items: [...prev.items, blankItem()],
                      }))
                    }
                  >
                    <i className="ri-add-line me-1"></i>Add task
                  </button>

                  <div className="d-flex justify-content-between gap-2 border-top pt-3">
                    <div>
                      {editing.Id && (
                        <button
                          type="button"
                          className="btn btn-soft-danger"
                          onClick={() => setDeleteOpen(true)}
                        >
                          Delete template
                        </button>
                      )}
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-light"
                        onClick={() => setEditing(null)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-success"
                        disabled={saving}
                        onClick={handleSave}
                      >
                        {saving ? "Saving..." : "Save template"}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
}
